if (!customElements.get('meris-countdown')) {
  customElements.define('meris-countdown', class extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.endDate).getTime();
      if (Number.isNaN(this.end)) return;
      this.update();
      this.timer = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() { window.clearInterval(this.timer); }

    update() {
      const remaining = Math.max(0, this.end - Date.now());
      const values = {
        days: Math.floor(remaining / 86400000),
        hours: Math.floor((remaining % 86400000) / 3600000),
        minutes: Math.floor((remaining % 3600000) / 60000),
        seconds: Math.floor((remaining % 60000) / 1000)
      };

      Object.entries(values).forEach(([key, value]) => {
        const target = this.querySelector(`[data-${key}]`);
        if (target) target.textContent = String(value).padStart(2, '0');
      });

      if (remaining === 0) {
        window.clearInterval(this.timer);
        this.querySelector('[data-expired]')?.removeAttribute('hidden');
        this.querySelector('.meris-countdown__grid')?.setAttribute('hidden', '');
        if (this.dataset.hideExpired === 'true') this.setAttribute('hidden', '');
      }
    }
  });
}

if (!customElements.get('meris-product-tabs')) {
  customElements.define('meris-product-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.list = this.querySelector('[role="tablist"]');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          this.select(next, true);
        });
      });

      this.list?.addEventListener('wheel', event => {
        if (this.list.scrollWidth <= this.list.clientWidth) return;
        const movement = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        const max = this.list.scrollWidth - this.list.clientWidth;
        const canMove = movement < 0 ? this.list.scrollLeft > 0 : this.list.scrollLeft < max - 1;
        if (!canMove) return;
        event.preventDefault();
        this.list.scrollLeft += movement;
      }, { passive: false });
    }

    select(index, focus = false) {
      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        if (panel) panel.hidden = !selected;
      });
      if (focus) this.tabs[index]?.focus();
    }
  });
}

if (!customElements.get('meris-subscription-widget')) {
  customElements.define('meris-subscription-widget', class extends HTMLElement {
    connectedCallback() {
      this.planInput = this.querySelector('[data-selling-plan-input]');
      this.radios = [...this.querySelectorAll('input[type="radio"]')];
      if (!this.radios.some(radio => radio.checked) && this.radios[0]) this.radios[0].checked = true;
      this.radios.forEach(radio => radio.addEventListener('change', () => this.update()));
      this.update();
    }

    update() {
      const selected = this.radios.find(radio => radio.checked);
      if (!this.planInput || !selected) return;
      this.planInput.value = selected.value;
      this.planInput.disabled = selected.value === '';
      this.querySelectorAll('[data-purchase-option]').forEach(option => {
        option.toggleAttribute('data-selected', option.contains(selected));
      });
    }
  });
}

if (!customElements.get('meris-addons')) {
  customElements.define('meris-addons', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.productForm);
      if (!this.form) return;
      this.onSubmit = event => this.submit(event);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.querySelectorAll('[data-addon-variant]').forEach(selector => {
        selector.addEventListener('change', () => this.updatePrice(selector));
      });
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit, true);
    }

    updatePrice(selector) {
      if (selector.tagName !== 'SELECT') return;
      const card = selector.closest('.meris-addon-card');
      const option = selector.selectedOptions[0];
      const priceBox = card?.querySelector('.meris-addon-card__price');
      if (!priceBox || !option) return;

      const price = document.createElement('strong');
      price.dataset.addonPrice = '';
      price.textContent = option.dataset.priceDisplay || '';
      const nodes = [];
      const discount = Number(option.dataset.discount || 0);

      if (discount > 0) {
        const badge = document.createElement('em');
        badge.dataset.addonDiscount = '';
        badge.textContent = `${discount}% OFF`;
        nodes.push(badge);
      }
      nodes.push(price);
      if (discount > 0) {
        const compare = document.createElement('s');
        compare.dataset.addonCompare = '';
        compare.textContent = option.dataset.compareDisplay || '';
        nodes.push(compare);
      }
      priceBox.replaceChildren(...nodes);
    }

    selectedItems() {
      return [...this.querySelectorAll('.meris-addon-card')]
        .filter(card => card.querySelector('[data-addon-toggle]')?.checked)
        .map(card => ({ id: Number(card.querySelector('[data-addon-variant]')?.value), quantity: 1 }))
        .filter(item => Number.isFinite(item.id) && item.id > 0);
    }

    async submit(event) {
      if (this.bypass || this.busy) return;
      const addons = this.selectedItems();
      if (!addons.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.busy = true;

      const submitButton = this.form.querySelector('[type="submit"]');
      if (submitButton) submitButton.disabled = true;

      try {
        const formData = new FormData(this.form);
        const mainId = Number(formData.get('id'));
        if (!Number.isFinite(mainId) || mainId <= 0) throw new Error('No selected variant');

        const mainItem = {
          id: mainId,
          quantity: Number(formData.get('quantity') || 1)
        };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [mainItem, ...addons]
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add items');

        const drawer = document.querySelector('#cart-drawer');
        drawer?.addEventListener('cart-drawer:refreshed', () => drawer.show?.(), { once: true });
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (error) {
        console.error('[Meris add-ons]', error);
        this.bypass = true;
        this.form.requestSubmit();
        this.bypass = false;
      } finally {
        this.busy = false;
        if (submitButton) submitButton.disabled = false;
      }
    }
  });
}

if (!customElements.get('meris-video-carousel')) {
  customElements.define('meris-video-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      if (!this.track) return;
      this.previous?.addEventListener('click', () => this.move(-1));
      this.next?.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
    }

    move(direction) {
      const slide = this.track.querySelector('[data-slide]');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * ((slide?.getBoundingClientRect().width || this.track.clientWidth) + gap), behavior: 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.previous) this.previous.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  });
}
